import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import EntryFields from "@/components/EntryFields";
import { format, parseISO } from "date-fns";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export default function EditEntryDialog({ entry, open, onOpenChange, helpers, workTypes, onSaved }) {
  const [date, setDate] = useState(new Date());
  const [helper, setHelper] = useState("");
  const [hours, setHours] = useState("8");
  const [workType, setWorkType] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (entry && open) {
      try {
        setDate(entry.date ? parseISO(entry.date) : new Date());
      } catch {
        setDate(new Date());
      }
      setHelper(entry.helper_name || "");
      setHours(String(entry.hours || 8));
      setWorkType(entry.work_type || "");
    }
  }, [entry, open]);

  const save = async () => {
    if (!helper.trim()) return toast.error("Bitte einen Helfer auswählen.");
    if (!workType.trim()) return toast.error("Bitte die Art der Bauarbeiten angeben.");
    setSaving(true);
    try {
      await api.put(`/entries/${entry.id}`, {
        date: format(date, "yyyy-MM-dd"),
        helper_name: helper.trim(),
        hours: parseInt(hours, 10),
        work_type: workType.trim(),
      });
      toast.success("Eintrag aktualisiert.");
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white border-[#E2E2E2] max-w-2xl" data-testid="edit-entry-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-[#E6017D]">Eintrag bearbeiten</DialogTitle>
          <DialogDescription className="text-[#5F6368]">Ändere Datum, Stunden, Helfer oder Art der Bauarbeiten.</DialogDescription>
        </DialogHeader>
        <div className="py-2">
          <EntryFields
            date={date} setDate={setDate}
            hours={hours} setHours={setHours}
            helper={helper} setHelper={setHelper}
            workType={workType} setWorkType={setWorkType}
            helpers={helpers} workTypes={workTypes}
            dateTestId="edit-date-trigger"
            hoursTestId="edit-hours-trigger"
            helperTestId="edit-helper-combobox"
            workTypeTestId="edit-worktype-combobox"
          />
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="edit-cancel-button"
            className="bg-white border-[#E2E2E2] text-[#212529] hover:bg-[#FCE7F3]">Abbrechen</Button>
          <Button onClick={save} disabled={saving} data-testid="edit-save-button"
            className="bg-[#E6017D] hover:bg-[#C4016A] text-white">
            {saving ? <Loader2 className="animate-spin" size={18} /> : "Speichern"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
